import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAgentContext } from '../contexts/AgentContext';
import { getBackendUrl } from '../lib/backend';

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user, refreshUserData } = useAgentContext();

  useEffect(() => {
    if (isAuthenticated && !user) {
      refreshUserData();
    }
  }, [isAuthenticated, user, refreshUserData]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsMenuOpen(false);
  };

  const handleLogin = () => {
    window.location.href = `${getBackendUrl()}/auth/google`;
    setIsMenuOpen(false);
  };

  const NavLink = ({
    to,
    children,
    isMobile = false,
  }: {
    to: string;
    children: React.ReactNode;
    isMobile?: boolean;
  }) => {
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        className={`px-4 py-2 rounded-lg transition-colors ${
          isActive ? 'bg-primary text-white' : 'text-primary hover:bg-primary/10'
        } ${isMobile ? 'block w-full text-left' : ''}`}
        onClick={() => isMobile && setIsMenuOpen(false)}
      >
        {children}
      </Link>
    );
  };

  return (
    <nav className="bg-background shadow-md relative overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex-shrink-0">
            <Link to="/" className="text-2xl font-bold text-primary flex items-center gap-2">
              <img src="/logo192.png" alt="Asistente Fácil Logo" className="w-10 h-10" />
              <span>Asistente Fácil</span>
            </Link>
          </div>

          <div className="hidden md:flex md:items-center md:space-x-4">
            {isAuthenticated ? (
              <>
                <NavLink to="/dashboard">Inicio</NavLink>
                <NavLink to="/preferences">Preferencias</NavLink>
                <NavLink to="/subscription">Planes</NavLink>
                <NavLink to="/customers">Clientes</NavLink>
                <a
                  href="https://calendar.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg transition-colors text-primary hover:bg-primary/10"
                >
                  Calendario
                </a>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 rounded-lg bg-secondary text-white hover:bg-secondary/90 transition-colors"
                >
                  Cerrar Sesión
                </button>
              </>
            ) : (
              <button
                onClick={handleLogin}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                Iniciar con Google
              </button>
            )}
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-primary hover:bg-primary/10"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">Abrir menú principal</span>
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        <div
          className={`${
            isMenuOpen ? 'block' : 'hidden'
          } md:hidden fixed top-16 left-0 right-0 bg-background shadow-lg z-50 w-full max-h-[calc(100vh-4rem)] overflow-y-auto`}
        >
          <div className="px-4 py-3 space-y-3">
            {isAuthenticated ? (
              <div className="flex flex-col space-y-3">
                <NavLink to="/dashboard" isMobile>
                  Inicio
                </NavLink>
                <NavLink to="/preferences" isMobile>
                  Preferencias
                </NavLink>
                <NavLink to="/subscription" isMobile>
                  Planes
                </NavLink>
                <NavLink to="/customers" isMobile>
                  Clientes
                </NavLink>
                <a
                  href="https://calendar.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg transition-colors text-primary hover:bg-primary/10 block w-full text-left"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Calendario
                </a>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 rounded-lg bg-secondary text-white hover:bg-secondary/90 transition-colors"
                >
                  Cerrar Sesión
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="w-full text-left px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                Iniciar con Google
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
